import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Save, Lock } from 'lucide-react';
import { Gate } from '../../AccessStudio';

// Grade grid: students × the semester's exams for one subject.
// Closed exams are read-only (grayed); scores are clamped to the
// subject's max both here and on the server.
const GradeEntrySheet = ({ apiCall, year, className, mappings, periods, showSuccess, showError }) => {
  const [classSubjectId, setClassSubjectId] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [sections, setSections] = useState([]);
  const [sectionCode, setSectionCode] = useState('');
  const [sheet, setSheet] = useState(null);
  const [scores, setScores] = useState({}); // `${studentId}|${examId}` -> value
  const [dirty, setDirty] = useState(false);

  // Subjects that are graded per domain aren't enterable themselves —
  // scores go on their domains and the parent is the computed sum.
  // MaxGrade 0 rows exist only for the competence carnet.
  const enterable = useMemo(() => mappings.filter(m => !m.ChildCount && Number(m.MaxGrade) > 0), [mappings]);

  useEffect(() => {
    setClassSubjectId(prev => enterable.some(m => String(m.ClassSubjectID) === String(prev)) ? prev : (enterable[0]?.ClassSubjectID || ''));
  }, [enterable]);
  useEffect(() => {
    setPeriodId(prev => periods.some(p => String(p.PeriodID) === String(prev)) ? prev : (periods[0]?.PeriodID || ''));
  }, [periods]);

  useEffect(() => {
    if (!className) return;
    apiCall(`/academic/sections?academicYear=${year}&className=${encodeURIComponent(className)}`)
      .then(s => setSections(s.filter(x => x.IsActive))).catch(() => setSections([]));
    setSectionCode('');
  }, [apiCall, year, className]);

  const load = useCallback(async () => {
    if (!classSubjectId || !periodId) { setSheet(null); return; }
    try {
      let url = `/academic/grading/sheet?classSubjectId=${classSubjectId}&periodId=${periodId}`;
      if (sectionCode) url += `&sectionCode=${encodeURIComponent(sectionCode)}`;
      const data = await apiCall(url);
      setSheet(data);
      const map = {};
      data.grades.forEach(g => { map[`${g.StudentID}|${g.ExamID}`] = (g.Score === null && g.IsExempt) ? 'D' : g.Score; });
      setScores(map);
      setDirty(false);
    } catch (err) { showError(err.message); }
  }, [apiCall, classSubjectId, periodId, sectionCode, showError]);

  useEffect(() => { load(); }, [load]);

  const setScore = (studentId, examId, value) => {
    // 'D' = Dispensé (exempt); otherwise digits only, clamped to the max
    const t = String(value).trim().toUpperCase();
    if (t === 'D') value = 'D';
    else {
      value = String(value).replace(/[^0-9.]/g, '');
      const n = parseFloat(value);
      if (!isNaN(n) && sheet) {
        if (n > Number(sheet.maxGrade)) value = String(sheet.maxGrade);
        if (n < 0) value = '0';
      }
    }
    setScores(s => ({ ...s, [`${studentId}|${examId}`]: value }));
    setDirty(true);
  };

  // Dispensé for the whole class on one exam (e.g. no theatre exam held)
  const setAllExempt = (exam) => {
    if (!sheet || !exam.IsOpen) return;
    if (!window.confirm(`Mark ALL students "D" (Dispensé) for ${isDirect ? 'this mark' : exam.ExamName}?`)) return;
    setScores(s => {
      const next = { ...s };
      for (const stu of sheet.students) next[`${stu.StudentID}|${exam.ExamID}`] = 'D';
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    try {
      const openExams = sheet.exams.filter(e => e.IsOpen);
      const entries = [];
      for (const stu of sheet.students) {
        for (const e of openExams) {
          const key = `${stu.StudentID}|${e.ExamID}`;
          if (key in scores) {
            const raw = scores[key];
            entries.push({ studentId: stu.StudentID, examId: e.ExamID,
              score: raw === '' || raw === null ? null : (String(raw).toUpperCase() === 'D' ? 'D' : parseFloat(raw)) });
          }
        }
      }
      const r = await apiCall('/academic/grading/grades', {
        method: 'POST',
        body: JSON.stringify({ classSubjectId: parseInt(classSubjectId), entries })
      });
      showSuccess(r.message);
      setDirty(false);
    } catch (err) { showError(err.message); }
  };

  // Semester average for the row (weighted by exam %, graded exams only)
  const semAvg = useMemo(() => (studentId) => {
    if (!sheet?.exams?.length) return null;
    let wSum = 0, acc = 0;
    for (const e of sheet.exams) {
      const v = scores[`${studentId}|${e.ExamID}`];
      if (v !== '' && v !== null && v !== undefined && !isNaN(parseFloat(v))) {
        wSum += Number(e.WeightPct);
        acc += (parseFloat(v) / Number(sheet.maxGrade)) * Number(e.WeightPct);
      }
    }
    return wSum > 0 ? Math.round((acc / wSum) * Number(sheet.maxGrade) * 100) / 100 : null;
  }, [sheet, scores]);

  if (!mappings.length) {
    return <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400 text-sm">
      No subjects assigned to {className} yet — add them in Exams Setup.</div>;
  }

  const anyOpen = sheet?.exams?.some(e => e.IsOpen);
  // direct period = one hidden 100% mark; show a single "Note" column
  const isDirect = sheet?.exams?.length === 1 && !!sheet.exams[0].IsDirect;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-3">
        <select value={classSubjectId} onChange={e => setClassSubjectId(e.target.value)} className="border rounded-lg px-3 py-2 text-sm w-full sm:w-auto">
          {enterable.map(m => (
            <option key={m.ClassSubjectID} value={m.ClassSubjectID}>
              {m.ParentSubjectName ? `${m.ParentSubjectName} — ${m.SubjectName}` : `${m.SubjectName} (coef ${m.Coefficient})`}
            </option>
          ))}
        </select>
        <select value={periodId} onChange={e => setPeriodId(e.target.value)} className="border rounded-lg px-3 py-2 text-sm w-full sm:w-auto">
          {periods.map(p => <option key={p.PeriodID} value={p.PeriodID}>{p.PeriodName} ({p.WeightPct}%)</option>)}
        </select>
        <select value={sectionCode} onChange={e => setSectionCode(e.target.value)} className="border rounded-lg px-3 py-2 text-sm w-full sm:w-auto">
          <option value="">All sections</option>
          {sections.map(s => <option key={s.SectionID} value={s.SectionCode}>{className} {s.SectionCode}</option>)}
        </select>
        {sheet && <span className="text-xs text-gray-500">graded out of <b>{sheet.maxGrade}</b></span>}
        <div className="hidden sm:block flex-1" />
        <Gate permission="academic.grading.enter" label="Save Grades">
          <button onClick={save} disabled={!dirty || !anyOpen}
            className="w-full sm:w-auto justify-center px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
            <Save size={15} /> Save Grades
          </button>
        </Gate>
      </div>

      {sheet && sheet.exams.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          No exams configured for this semester — create them in Exams Setup.
        </div>
      )}
      {sheet && sheet.exams.length > 0 && !anyOpen && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          All exams of this semester are closed — an administrator must open one before grades can be entered.
        </div>
      )}

      {sheet && sheet.exams.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50 z-20">Student</th>
                {sheet.exams.map(e => (
                  <th key={e.ExamID} className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    <span className="flex items-center justify-center gap-1">
                      {isDirect ? 'Note' : e.ExamName}{!e.IsOpen && <Lock size={11} className="text-red-400" />}
                    </span>
                    <span className="font-normal normal-case">{isDirect ? `/${sheet.maxGrade}` : `${e.WeightPct}% · /${sheet.maxGrade}`}</span>
                    <Gate permission="academic.grading.enter" label="Exempt Whole Exam">
                      <button onClick={() => setAllExempt(e)} disabled={!e.IsOpen}
                        title="Dispensé — mark D for the whole class"
                        className="block mx-auto mt-0.5 px-1.5 text-[10px] normal-case border border-amber-300 text-amber-700 rounded hover:bg-amber-50 disabled:opacity-40">
                        D → all
                      </button>
                    </Gate>
                  </th>
                ))}
                {!isDirect && <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Period /{sheet.maxGrade}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sheet.students.map(stu => {
                const avg = semAvg(stu.StudentID);
                return (
                  <tr key={stu.StudentID} className="hover:bg-gray-50">
                    <td className="px-3 py-1 font-medium text-gray-800 whitespace-nowrap sticky left-0 bg-white z-10">
                      {stu.LastName} {stu.FirstName}
                      {!sectionCode && stu.Section && <span className="ml-1 text-xs text-gray-400">({stu.Section})</span>}
                    </td>
                    {sheet.exams.map(e => {
                      const val = scores[`${stu.StudentID}|${e.ExamID}`] ?? '';
                      return (
                        <td key={e.ExamID} className="px-2 py-1 text-center">
                          <input
                            type="text" inputMode="decimal" maxLength={6}
                            disabled={!e.IsOpen}
                            title="Score, or D = Dispensé (exempt)"
                            value={val}
                            onChange={ev => setScore(stu.StudentID, e.ExamID, ev.target.value)}
                            className={`w-16 border rounded px-1 py-0.5 text-sm text-center ${!e.IsOpen ? 'bg-gray-100 text-gray-400' : ''} ${val === 'D' ? 'text-amber-700 font-semibold bg-amber-50' : ''}`}
                          />
                        </td>
                      );
                    })}
                    {!isDirect && (
                      <td className={`px-3 py-1 text-center font-semibold ${avg === null ? 'text-gray-300' : avg < Number(sheet.maxGrade) / 2 ? 'text-red-600' : 'text-green-700'}`}>
                        {avg ?? '—'}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GradeEntrySheet;
